import AnalysisPricesLoader from "@/components/AnalysisPricesLoader";
import { lazy, Suspense, useEffect, type ComponentType, type ReactNode } from 'react';
import { ClientOnly } from '@/components/ClientOnly';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { lazyComponent } from '@/lib/lazyComponent';
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AdminPreviewProvider } from "@/contexts/AdminPreviewContext";
import { AdminImpersonationProvider } from "@/contexts/AdminImpersonationContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { GeolocationProvider } from "@/contexts/GeolocationContext";
import { WorkspaceProvider } from "@/contexts/WorkspaceContext";
import { TrackingManager } from "@/components/TrackingManager";
import ScrollToTop from "@/components/ScrollToTop";
import { toast } from "sonner";
import { LanguageLayout, StripLangRedirect } from "@/components/LanguageLayout";
import { ConditionalDimitrisChat } from "@/components/ConditionalDimitrisChat";
import { isLinksHostMode } from "@/seo/linksHost";
import { ShareThemeToggle } from "@/components/ShareThemeToggle";
import { BrandLoader } from "@/components/brand/BrandLoader";

// Lazy load heavy components not needed for initial public page render
const DashboardLayout = lazyComponent("DashboardLayout", () => import("@/components/DashboardLayout").then(m => ({ default: m.DashboardLayout })));
const ProtectedRoute = lazyComponent("ProtectedRoute", () => import("@/components/ProtectedRoute").then(m => ({ default: m.ProtectedRoute })));
const AdminRoute = lazyComponent("AdminRoute", () => import("@/components/AdminRoute").then(m => ({ default: m.AdminRoute })));
const Auth = lazyWithRetry("Auth", () => import("@/components/Auth").then(m => ({ default: m.Auth })));
const Unsubscribe = lazyWithRetry("Unsubscribe", () => import("./pages/Unsubscribe"));

// Lazy load non-critical UI components (popups, chat, cookie consent)
// Cookie banner uses lazyWithRetry so transient chunk-load failures don't
// leave the banner silently missing (GDPR compliance).
const CookieConsent = lazyWithRetry("CookieConsent", () =>
  import("@/components/CookieConsent").then((m) => ({ default: m.CookieConsent }))
);
// Global V3 run watcher: completion banners must appear on
// every route (public pages and the free flow included), not just dashboards.
const V3RunWatcher = lazyWithRetry("V3RunWatcher", () => import("@/components/v3/V3RunWatcher"));
// Ends a sign-in after 8 hours idle or 12 hours in total (src/lib/sessionPolicy.ts).
const SessionGuard = lazyWithRetry("SessionGuard", () => import("@/components/SessionGuard"));
const PromoPopup = lazyComponent("PromoPopup", () => import("@/components/PromoPopup").then(m => ({ default: m.PromoPopup })));
const ReferralSidePopup = lazyComponent("ReferralSidePopup", () => import("@/components/ReferralSidePopup").then(m => ({ default: m.ReferralSidePopup })));
const ReferralDiscountBanner = lazyComponent("ReferralDiscountBanner", () => import("@/components/referral/ReferralDiscountBanner").then(m => ({ default: m.ReferralDiscountBanner })));
const ExitIntentPopup = lazyComponent("ExitIntentPopup", () => import("@/components/newsletter/ExitIntentPopup").then(m => ({ default: m.ExitIntentPopup })));

// Component to render popups only on desktop - completely disabled on mobile for performance
/**
 * Report links someone was sent (share, embed, public comparison and free
 * audit results) are the agency's deliverable, often white-labelled: no
 * EyeCaptain promo, referral or newsletter popups on them. Nor on the bio
 * link page (links.eyecaptain.io), which is only a list of links.
 */
const REPORT_LINK_PATHS = /^\/(shared|embed|compare\/s|free-cro-results|free-cro-v3\/results|website-audit-results)\//;

const DesktopOnlyPopups = lazyComponent("DesktopOnlyPopups", () => import("@/hooks/use-mobile").then(({ useIsMobile }) => {
  function DesktopOnlyPopupsInner() {
    const isMobile = useIsMobile();
    const { pathname } = useLocation();
    if (isMobile || LINKS_HOST_MODE || REPORT_LINK_PATHS.test(pathname.replace(/^\/(el|en)(?=\/)/, ""))) return null;
    return (
      <Suspense fallback={null}>
        <PromoPopup />
        <ReferralSidePopup />
        <ReferralDiscountBanner />
        <ExitIntentPopup />
      </Suspense>
    );
  }
  return { default: DesktopOnlyPopupsInner };
}));

// Lazy load all pages for better performance
const Index = lazyWithRetry("Index", () => import("./pages/Index"));
const ComparisonPage = lazyWithRetry("ComparisonPage", () => import("./pages/seo/ComparisonPage"));
const ShopifySolution = lazyWithRetry("ShopifySolution", () => import("./pages/seo/ShopifySolution"));
const PlatformSolution = lazyWithRetry("PlatformSolution", () => import("./pages/seo/PlatformSolution"));
const WhatIsEyeCaptain = lazyComponent("WhatIsEyeCaptain", () => import("./pages/WhatIsEyeCaptain"));
const CroChecklist = lazyWithRetry("CroChecklist", () => import("./pages/seo/CroChecklist"));
const CroAuditGuide = lazyWithRetry("CroAuditGuide", () => import("./pages/seo/CroAuditGuide"));
const Agencies = lazyComponent("Agencies", () => import("./pages/Agencies"));
const Contact = lazyComponent("Contact", () => import("./pages/Contact"));
const Pricing = lazyWithRetry("Pricing", () => import("./pages/Pricing"));
const Checkout = lazyComponent("Checkout", () => import("./pages/Checkout"));

const WhatIsCRO = lazyComponent("WhatIsCRO", () => import("./pages/WhatIsCRO"));
const HowItWorks = lazyComponent("HowItWorks", () => import("./pages/HowItWorks"));
const Workspace = lazyWithRetry("Workspace", () => import("./pages/Workspace"));
const Dashboard = lazyWithRetry("Dashboard", () => import("./pages/Dashboard"));
const AllProjects = lazyWithRetry("AllProjects", () => import("./pages/AllProjects"));
const NewProject = lazyComponent("NewProject", () => import("./pages/NewProject"));
const ProjectView = lazyWithRetry("ProjectView", () => import("./pages/ProjectView"));
const Admin = lazyWithRetry("Admin", () => import("./pages/Admin"));

// Account page removed - redirects to /workspace
const Settings = lazyComponent("Settings", () => import("./pages/Settings"));
const Plans = lazyComponent("Plans", () => import("./pages/Plans"));
const ExtensionConnect = lazyComponent("ExtensionConnect", () => import("./pages/ExtensionConnect"));
// Dev only: renders Settings screens with fixtures, no sign-in needed. Not in production builds.
const BillingPreview = import.meta.env.DEV ? lazy(() => import("./pages/dev/BillingPreview")) : null;
const AgentPreview = import.meta.env.DEV ? lazy(() => import("./pages/dev/AgentPreview")) : null;
const NotFound = lazyComponent("NotFound", () => import("./pages/NotFound"));
const PaymentSuccess = lazyComponent("PaymentSuccess", () => import("./pages/PaymentSuccess"));
const PromoLanding = lazyComponent("PromoLanding", () => import("./pages/PromoLanding"));
const LinkInBio = lazyComponent("LinkInBio", () => import("./pages/LinkInBio"));
/* links.eyecaptain.io (or a preview with ?links_host=1) shows only the links page. Never on the server (prerender). */
const LINKS_HOST_MODE = typeof window !== "undefined" && isLinksHostMode(window.location.hostname, window.location.search);
const SpecialOffer = lazyComponent("SpecialOffer", () => import("./pages/SpecialOffer"));
const ResetPassword = lazyComponent("ResetPassword", () => import("./pages/ResetPassword"));
const Onboarding = lazyComponent("Onboarding", () => import("./pages/Onboarding"));
const AgencyInterest = lazyComponent("AgencyInterest", () => import("./pages/AgencyInterest"));
const FAQ = lazyComponent("FAQ", () => import("./pages/FAQ"));
const EcommerceSolution = lazyComponent("EcommerceSolution", () => import("./pages/EcommerceSolution"));
const LeadGenSolution = lazyComponent("LeadGenSolution", () => import("./pages/LeadGenSolution"));
const AIHeatmaps = lazyComponent("AIHeatmaps", () => import("./pages/AIHeatmaps"));
const Neuromarketing = lazyComponent("Neuromarketing", () => import("./pages/Neuromarketing"));
const CROTechniques = lazyComponent("CROTechniques", () => import("./pages/CROTechniques"));
const SharedProjectView = lazyComponent("SharedProjectView", () => import("./pages/SharedProjectView"));
const EmbedProjectView = lazyComponent("EmbedProjectView", () => import("./pages/EmbedProjectView"));
const Signup = lazyComponent("Signup", () => import("./pages/Signup"));
const Clients = lazyComponent("Clients", () => import("./pages/Clients"));
const ClientDetail = lazyComponent("ClientDetail", () => import("./pages/ClientDetail"));
const CompetitorCompare = lazyComponent("CompetitorCompare", () => import("./pages/CompetitorCompare"));
const AgentFunnel = lazyComponent("AgentFunnel", () => import("./pages/AgentFunnel"));
const AiAgent = lazyComponent("AiAgent", () => import("./pages/AiAgent"));
const FigmaAnalysisNew = lazyComponent("FigmaAnalysisNew", () => import("./pages/FigmaAnalysisNew"));
const FigmaAnalysisView = lazyComponent("FigmaAnalysisView", () => import("./pages/FigmaAnalysisView"));
const LiveHeatmapsNew = lazyComponent("LiveHeatmapsNew", () => import("./pages/LiveHeatmapsNew"));
const LiveHeatmapsView = lazyComponent("LiveHeatmapsView", () => import("./pages/LiveHeatmapsView"));
const AbTestNew = lazyComponent("AbTestNew", () => import("./pages/AbTestNew"));
const AbTestView = lazyComponent("AbTestView", () => import("./pages/AbTestView"));
const Experiments = lazyComponent("Experiments", () => import("./pages/Experiments"));
const ExperimentNew = lazyComponent("ExperimentNew", () => import("./pages/ExperimentNew"));
const CompetitorComparisonSolution = lazyComponent("CompetitorComparisonSolution", () => import("./pages/CompetitorComparisonSolution"));
const Blog = lazyWithRetry("Blog", () => import("./pages/Blog"));
const BlogArticle = lazyWithRetry("BlogArticle", () => import("./pages/BlogArticle"));
const AuthorPage = lazyWithRetry("AuthorPage", () => import("./pages/AuthorPage"));

const ContentAnalysisSolution = lazyComponent("ContentAnalysisSolution", () => import("./pages/ContentAnalysisSolution"));
const TechnicalAnalysisSolution = lazyComponent("TechnicalAnalysisSolution", () => import("./pages/TechnicalAnalysisSolution"));
const CroPageAudit = lazyComponent("CroPageAudit", () => import("./pages/CroPageAudit"));
const SaasSolution = lazyComponent("SaasSolution", () => import("./pages/SaasSolution"));
const BookingSolution = lazyComponent("BookingSolution", () => import("./pages/BookingSolution"));
const InformationSolution = lazyComponent("InformationSolution", () => import("./pages/InformationSolution"));
const FunnelAnalysisSolution = lazyComponent("FunnelAnalysisSolution", () => import("./pages/FunnelAnalysisSolution"));
const UserAgentSolution = lazyComponent("UserAgentSolution", () => import("./pages/UserAgentSolution"));
// Upcoming features: public pages with an "Upcoming feature" badge (Admin › Releases decides when they go live).
const LiveHeatmapsFeature = lazyComponent("LiveHeatmapsFeature", () => import("./pages/LiveHeatmapsFeature"));
const AbTestingFeature = lazyComponent("AbTestingFeature", () => import("./pages/AbTestingFeature"));
const AnalyticsIntegrations = lazyComponent("AnalyticsIntegrations", () => import("./pages/AnalyticsIntegrations"));
const VisualMockups = lazyComponent("VisualMockups", () => import("./pages/VisualMockups"));
const AiAgentFeature = lazyComponent("AiAgentFeature", () => import("./pages/AiAgentFeature"));
const FigmaDesignAuditFeature = lazyComponent("FigmaDesignAuditFeature", () => import("./pages/FigmaDesignAuditFeature"));
const HeatmapAnalysisPage = lazyComponent("HeatmapAnalysisPage", () => import("./pages/HeatmapAnalysisPage"));
const HeatmapResultsPage = lazyComponent("HeatmapResultsPage", () => import("./pages/HeatmapResultsPage"));
const FreeCROResults = lazyComponent("FreeCROResults", () => import("./pages/FreeCROResults"));
const FreeCROV3Results = lazyComponent("FreeCROV3Results", () => import("./pages/FreeCROV3Results"));
const FreeCroAudit = lazyComponent("FreeCroAudit", () => import("./pages/FreeCroAudit"));
const WebsiteAuditResults = lazyComponent("WebsiteAuditResults", () => import("./pages/WebsiteAuditResults"));
const Terms = lazyComponent("Terms", () => import("./pages/Terms"));
const Privacy = lazyComponent("Privacy", () => import("./pages/Privacy"));
const LegalPage = lazyComponent("LegalPage", () => import("./pages/LegalPage"));
const GA4OAuthCallback = lazyComponent("GA4OAuthCallback", () => import("./pages/GA4OAuthCallback"));
const TechnicalAnalysis = lazyComponent("TechnicalAnalysis", () => import("./pages/TechnicalAnalysis"));
const EyeCaptainPresentation = lazyComponent("EyeCaptainPresentation", () => import("./pages/EyeCaptainPresentation"));
const HeatmapGuide = lazyComponent("HeatmapGuide", () => import("./pages/HeatmapGuide"));
const ABTestingGuide = lazyComponent("ABTestingGuide", () => import("./pages/ABTestingGuide"));
const EcommerceCROGuide = lazyComponent("EcommerceCROGuide", () => import("./pages/EcommerceCROGuide"));
const PlatformGuide = lazyWithRetry("PlatformGuide", () => import("./pages/seo/PlatformGuide"));

// Analytics Admin pages

// Minimal inline loading fallback - avoids importing SplashScreen into the main bundle
// Painted in the public site's palette (HOME_THEME), which is what most first loads land on.
const PageLoader = () => <BrandLoader />;


// Simple loader for embed pages without branding
const EmbedLoader = () => <BrandLoader />;


type RouterComponent = ComponentType<{ children?: ReactNode }>;

/**
 * `Router` is BrowserRouter in the browser; the build-time prerender
 * (src/entry-server.tsx) passes a router fixed to the page's URL, so the
 * server and the browser render the very same tree.
 */
const App = ({ Router = BrowserRouter }: { Router?: RouterComponent }) => {
  // Lazy load non-critical CSS (print styles, decorative animations, scrollbars)
  useEffect(() => {
    import('./styles/non-critical.css');
    // Clear chunk reload flag on successful load
    sessionStorage.removeItem('chunk_reload');
  }, []);

  // Global unhandled rejection handler to prevent blank screens from dynamic import failures
  useEffect(() => {
    const handleRejection = (event: PromiseRejectionEvent) => {
      if (event.reason?.message?.includes('dynamically imported module') || 
          event.reason?.message?.includes('Failed to fetch') ||
          event.reason?.message?.includes('Loading chunk')) {
        console.error("Dynamic import failed:", event.reason);
        // Auto-reload once for chunk loading failures
        const hasReloaded = sessionStorage.getItem('chunk_reload');
        if (!hasReloaded) {
          sessionStorage.setItem('chunk_reload', '1');
          window.location.reload();
        } else {
          toast.error("The page needs a refresh. Press Ctrl+Shift+R.");
        }
        event.preventDefault();
      }
    };

    window.addEventListener("unhandledrejection", handleRejection);
    return () => window.removeEventListener("unhandledrejection", handleRejection);
  }, []);

  return (
    <ErrorBoundary>
      {/*
        The one Suspense boundary for route loading, deliberately above every
        provider. On a prerendered page React hydrates a Suspense boundary in
        its own pass, after everything outside it; anything outside that
        updates in the meantime (a provider finishing its auth check, a query
        settling) makes React give up on the server HTML and render the whole
        page again. With the providers inside, nothing outside can update and
        the page hydrates in a single pass. Do not add another boundary around
        the routes: it would open that gap again.
      */}
      <Suspense fallback={<PageLoader />}>
        <AppInner Router={Router} />
      </Suspense>
    </ErrorBoundary>
  );
};

function AppInner({ Router }: { Router: RouterComponent }) {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <GeolocationProvider>
        <AdminPreviewProvider>
        <AdminImpersonationProvider>
          <TooltipProvider>
          <ClientOnly>
            <Toaster />
            <Sonner />
          </ClientOnly>
          <Router>
          <ScrollToTop />
          <TrackingManager />
          <ThemeProvider>
          <WorkspaceProvider>
          {/* Visitor-specific UI: never in the prerendered HTML (see ClientOnly). */}
          <ClientOnly>
            {/* Global popups - desktop only for better mobile PageSpeed */}
            <Suspense fallback={null}><DesktopOnlyPopups /></Suspense>
            {/* AI Assistant - hidden when user is authenticated (uses SidebarAssistant instead) */}
            <Suspense fallback={null}><ConditionalDimitrisChat /></Suspense>
            <ShareThemeToggle />
            <AnalysisPricesLoader />
          </ClientOnly>
          {/* Route chunks suspend up to the boundary in App, above the providers. */}
          {LINKS_HOST_MODE ? (
            /* links.eyecaptain.io: the links page at "/"; every other path there is a
               301 to eyecaptain.io at the edge (src/seo/linksHost.ts). */
            <Routes>
              <Route path="*" element={<LinkInBio />} />
            </Routes>
          ) : (
          <Routes>
              {/* Embed route - outside language prefix */}
              <Route path="/embed/:token" element={
                <Suspense fallback={<EmbedLoader />}>
                  <EmbedProjectView />
                </Suspense>
              } />

              {/* Presentation route - outside language prefix */}
              <Route path="/presentation" element={
                <Suspense fallback={<EmbedLoader />}>
                  <EyeCaptainPresentation />
                </Suspense>
              } />

              {BillingPreview && (
                <Route path="/dev/billing-preview" element={
                  <Suspense fallback={<PageLoader />}>
                    <BillingPreview />
                  </Suspense>
                } />
              )}
              {AgentPreview && (
                <Route path="/dev/agent-preview" element={
                  <Suspense fallback={<PageLoader />}>
                    <AgentPreview />
                  </Suspense>
                } />
              )}

              {/* Unsubscribe landing - outside language prefix, opens from email links */}
              <Route path="/unsubscribe" element={
                <Suspense fallback={<PageLoader />}>
                  <Unsubscribe />
                </Suspense>
              } />

              {/* Legacy /en/* and /el/* → strip prefix (SPA-internal fallback;
                  Netlify also 301-redirects at the edge). */}
              <Route path="/en/*" element={<StripLangRedirect />} />
              <Route path="/el/*" element={<StripLangRedirect />} />
              <Route path="/en" element={<Navigate to="/" replace />} />
              <Route path="/el" element={<Navigate to="/" replace />} />

              {/* Public pages — English-only, clean root URLs */}
              <Route path="/" element={<LanguageLayout />}>
                {/* Homepage */}
                <Route index element={<Index />} />
                
                {/* EN paths */}
                <Route path="analysis" element={<Navigate to="../solutions/ecommerce" replace />} />
                <Route path="benefits" element={<Navigate to="../how-it-works" replace />} />
                <Route path="agencies" element={<Agencies />} />
                <Route path="contact" element={<Contact />} />
                <Route path="pricing" element={<Pricing />} />
                <Route path="checkout/:planSlug" element={<Checkout />} />
                <Route path="checkout" element={<Checkout />} />
                <Route path="pricing/additional-pages" element={<Navigate to="../pricing" replace />} />
                <Route path="what-is-ux" element={<Navigate to="../what-is-cro" replace />} />
                <Route path="what-is-cro" element={<WhatIsCRO />} />
                <Route path="how-it-works" element={<HowItWorks />} />
                <Route path="what-is-eyecaptain" element={<WhatIsEyeCaptain />} />
                <Route path="faq" element={<FAQ />} />
                <Route path="solutions/ecommerce" element={<EcommerceSolution />} />
                <Route path="shopify-cro-audit" element={<ShopifySolution />} />
                <Route path="solutions/shopify" element={<Navigate to="/shopify-cro-audit" replace />} />
                {/* Platform pages: for search only, linked from the footer, not the menu. */}
                <Route path="woocommerce-cro-audit" element={<PlatformSolution slug="woocommerce" />} />
                <Route path="solutions/woocommerce" element={<Navigate to="/woocommerce-cro-audit" replace />} />
                <Route path="bigcommerce-cro-audit" element={<PlatformSolution slug="bigcommerce" />} />
                <Route path="solutions/bigcommerce" element={<Navigate to="/bigcommerce-cro-audit" replace />} />
                <Route path="magento-cro-audit" element={<PlatformSolution slug="magento" />} />
                <Route path="solutions/magento" element={<Navigate to="/magento-cro-audit" replace />} />
                <Route path="wix-cro-audit" element={<PlatformSolution slug="wix" />} />
                <Route path="solutions/wix" element={<Navigate to="/wix-cro-audit" replace />} />
                <Route path="squarespace-cro-audit" element={<PlatformSolution slug="squarespace" />} />
                <Route path="solutions/squarespace" element={<Navigate to="/squarespace-cro-audit" replace />} />
                <Route path="webflow-cro-audit" element={<PlatformSolution slug="webflow" />} />
                <Route path="solutions/webflow" element={<Navigate to="/webflow-cro-audit" replace />} />
                <Route path="cro-checklist" element={<CroChecklist />} />
                <Route path="cro-audit" element={<CroAuditGuide />} />
                <Route path="compare/:slug" element={<ComparisonPage />} />
                <Route path="solutions/lead-generation" element={<LeadGenSolution />} />
                <Route path="solutions/custom" element={<Navigate to="../contact" replace />} />
                <Route path="solutions/competitor-comparison" element={<CompetitorComparisonSolution />} />
                <Route path="solutions/content-analysis" element={<ContentAnalysisSolution />} />
                <Route path="solutions/technical-analysis" element={<TechnicalAnalysisSolution />} />
                <Route path="cro-page-audit" element={<CroPageAudit />} />
                <Route path="solutions/saas" element={<SaasSolution />} />
                <Route path="solutions/booking" element={<BookingSolution />} />
                <Route path="solutions/information-sites" element={<InformationSolution />} />
                <Route path="solutions/funnel-analysis" element={<FunnelAnalysisSolution />} />
                <Route path="solutions/user-agent" element={<UserAgentSolution />} />
                <Route path="solutions/figma-analysis" element={<Navigate to="../features/figma-design-audit" replace />} />
                <Route path="features/ai-heatmaps" element={<AIHeatmaps />} />
                <Route path="features/neuromarketing" element={<Neuromarketing />} />
                <Route path="features/cro-techniques" element={<CROTechniques />} />
                <Route path="features/live-heatmaps" element={<LiveHeatmapsFeature />} />
                <Route path="features/ab-testing" element={<AbTestingFeature />} />
                <Route path="features/analytics-integrations" element={<AnalyticsIntegrations />} />
                <Route path="features/visual-mockups" element={<VisualMockups />} />
                <Route path="features/ai-agent" element={<AiAgentFeature />} />
                <Route path="features/figma-design-audit" element={<FigmaDesignAuditFeature />} />
                <Route path="features/ai-captain" element={<Navigate to="../how-it-works" replace />} />
                <Route path="features/pagespeed-insights" element={<Navigate to="../solutions/technical-analysis" replace />} />
                <Route path="blog" element={<Blog />} />
                <Route path="cro-tools" element={<Navigate to="../what-is-cro" replace />} />
                <Route path="ux-audit-guide" element={<Navigate to="../free-audit" replace />} />
                <Route path="heatmap-analysis-guide" element={<HeatmapGuide />} />
                <Route path="ai-cro" element={<Navigate to="../how-it-works" replace />} />
                <Route path="ab-testing-guide" element={<ABTestingGuide />} />
                <Route path="ecommerce-cro-guide" element={<EcommerceCROGuide />} />
                <Route path="woocommerce-checkout-optimization" element={<PlatformGuide slug="woocommerce-checkout" />} />
                <Route path="shopify-product-page-best-practices" element={<PlatformGuide slug="shopify-product-page" />} />
                <Route path="webflow-landing-page-checklist" element={<PlatformGuide slug="webflow-landing-page" />} />
                <Route path="ecommerce-website-guide" element={<Navigate to="../ecommerce-cro-guide" replace />} />
                <Route path="leadgen-website-guide" element={<Navigate to="../solutions/lead-generation" replace />} />
                <Route path="ai-tools-guide" element={<Navigate to="../blog" replace />} />
                <Route path="ai-models-guide" element={<Navigate to="../blog" replace />} />
                <Route path="cro-tools-guide" element={<Navigate to="../what-is-cro" replace />} />
                <Route path="ai-marketing-guide" element={<Navigate to="../blog" replace />} />
                <Route path="ai-prompting-guide" element={<Navigate to="../blog" replace />} />
                <Route path="ai-hub" element={<Navigate to="../blog" replace />} />
                <Route path="analytics-hub" element={<Navigate to="../blog" replace />} />
                <Route path="blog/:slug" element={<BlogArticle />} />
                <Route path="authors/:slug" element={<AuthorPage />} />

                <Route path="free-cro-analysis" element={<Navigate to="../free-audit" replace />} />
                <Route path="free-cro-audit" element={<Navigate to="../free-audit" replace />} />
                <Route path="solutions" element={<Navigate to=".." replace />} />
                <Route path="free-cro-wizard" element={<Navigate to="../free-audit" replace />} />
                <Route path="free-cro-results/:shareId" element={<FreeCROResults />} />
                <Route path="free-cro-v3" element={<Navigate to="../free-audit" replace />} />
                <Route path="free-cro-v3/wizard" element={<Navigate to="../../free-audit" replace />} />
                <Route path="free-cro-v3/results/:shareId" element={<FreeCROV3Results />} />
                <Route path="free-audit" element={<FreeCroAudit />} />
                <Route path="free-website-audit" element={<Navigate to="../free-audit" replace />} />
                <Route path="website-audit-results/:shareId" element={<WebsiteAuditResults />} />
                <Route path="terms" element={<Terms />} />
                <Route path="rewards" element={<Navigate to="../signup" replace />} />
                <Route path="privacy" element={<Privacy />} />
                <Route path="cookies" element={<LegalPage doc="cookies" />} />
                <Route path="dpa" element={<LegalPage doc="dpa" />} />
                <Route path="subprocessors" element={<LegalPage doc="subprocessors" />} />
                <Route path="payment-success" element={<PaymentSuccess />} />
                <Route path="demo-signup" element={<Navigate to="../signup" replace />} />
                <Route path="promo/:code" element={<PromoLanding />} />
                <Route path="offer/:code" element={<SpecialOffer />} />
                <Route path="login" element={<Auth />} />
                {/* Common guesses for the sign-in page ended on a 404. */}
                <Route path="auth" element={<Navigate to="../login" replace />} />
                <Route path="signin" element={<Navigate to="../login" replace />} />
                <Route path="sign-in" element={<Navigate to="../login" replace />} />
                <Route path="register" element={<Navigate to="../signup" replace />} />
                <Route path="signup" element={<Signup />} />
                <Route path="agency-signup" element={<Navigate to="../signup" replace />} />
                <Route path="reset-password" element={<ResetPassword />} />
              </Route>

              {/* Short legacy paths (no /en or /el prefix) → canonical equivalents */}
              <Route path="/cro-techniques" element={<Navigate to="/features/cro-techniques" replace />} />
              <Route path="/neuromarketing" element={<Navigate to="/features/neuromarketing" replace />} />
              <Route path="/free-cro-audit" element={<Navigate to="/free-audit" replace />} />

              {/* Shared/embed routes - no language prefix */}
              <Route path="/shared/:token" element={<SharedProjectView />} />
              <Route path="/extension/connect" element={<ExtensionConnect />} />
              <Route path="/compare/s/:shareId" element={<CompetitorCompare publicView />} />
              <Route path="/figma/s/:shareId" element={<FigmaAnalysisView publicView />} />
              <Route path="/experiments/live-heatmaps/s/:shareId" element={<LiveHeatmapsView publicView />} />
              <Route path="/experiments/ab-tests/s/:shareId" element={<AbTestView publicView />} />
              {/* Google sends the person back here after allowing Google Analytics; the page finishes with their session. */}
              <Route path="/api/ga4-oauth-callback" element={<GA4OAuthCallback />} />
              <Route path="/api/oauth-callback" element={<GA4OAuthCallback />} />

              {/* Protected pages - no language prefix (authenticated area) */}
              <Route
                path="/heatmap-analysis"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <HeatmapAnalysisPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/heatmap-results"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <HeatmapResultsPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <Onboarding />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agency-interest"
                element={
                  <ProtectedRoute>
                    <AgencyInterest />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/workspace"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Workspace />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Dashboard />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <AllProjects />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/new-project"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <NewProject />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/project/:projectId"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <ProjectView />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/clients"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Clients />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route path="/my-brand" element={<Navigate to="/clients" replace />} />
              <Route path="/my-competitors" element={<Navigate to="/clients" replace />} />
              <Route
                path="/clients/:clientId"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <ClientDetail />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              {/* Competitor comparison V3: wizard shares the new-analysis shell; viewer by set id, public by share id. */}
              <Route path="/competitor-analysis" element={<Navigate to="/new-project?type=competitor_analysis" replace />} />
              <Route path="/competitor-results" element={<Navigate to="/projects" replace />} />
              <Route path="/competitor-results/:analysisId" element={<Navigate to="/projects" replace />} />
              <Route path="/competitor-summary/:projectId" element={<Navigate to="/projects" replace />} />
              <Route
                path="/competitors/:setId"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <CompetitorCompare />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              {/* Figma design audit: wizard and viewer (admins until figma_public is on; the pages check). */}
              <Route
                path="/figma/new"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <FigmaAnalysisNew />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/figma/:analysisId"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <FigmaAnalysisView />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              {/* Experiments (src/lib/experiments.ts): A/B tests and Live heatmaps on real visitors. Released in stages
                  (Admin › Releases); every page checks. An experiment is open to its workspace. */}
              <Route
                path="/experiments"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Experiments />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/experiments/new"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <ExperimentNew />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/experiments/ab-tests/new"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <AbTestNew />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/experiments/ab-tests/:experimentId"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <AbTestView />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/experiments/live-heatmaps/new"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <LiveHeatmapsNew />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/experiments/live-heatmaps/:studyId"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <LiveHeatmapsView />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              {/* AI Agent: released in stages (Admin › Releases); the page sends anyone it is not open to home. */}
              <Route
                path="/ai-agent"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <AiAgent />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              {/* User Agent & Funnel: released in stages (Admin › Releases). */}
              {["/agent", "/agent/new/:mode", "/agent/run/:runId"].map((path) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <ProtectedRoute>
                      <DashboardLayout>
                        <AgentFunnel />
                      </DashboardLayout>
                    </ProtectedRoute>
                  }
                />
              ))}
              <Route path="/analytics-admin/*" element={<Navigate to="/admin/growth" replace />} />
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <Admin />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/plans"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Plans />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account"
                element={
                  <ProtectedRoute>
                    <Navigate to="/workspace" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/technical-analysis/:analysisId?"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <TechnicalAnalysis />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route path="/share-earn" element={<Navigate to="/settings?tab=referral" replace />} />
              {/* Analytics Admin Routes */}


              <Route path="*" element={<NotFound />} />
            </Routes>
          )}
            <ClientOnly>
              {/* No banner on links.eyecaptain.io: one tap and the visitor is on
                  eyecaptain.io, where the banner asks. Without a choice the
                  consent defaults stand (analytics off in the EU), and the utm
                  params carry the source across. */}
              {!LINKS_HOST_MODE && <Suspense fallback={null}><CookieConsent /></Suspense>}
              <Suspense fallback={null}><V3RunWatcher /></Suspense>
              <Suspense fallback={null}><SessionGuard /></Suspense>
            </ClientOnly>
            </WorkspaceProvider>
            </ThemeProvider>
            </Router>
            </TooltipProvider>
          </AdminImpersonationProvider>
          </AdminPreviewProvider>
        </GeolocationProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}

export default App;
